/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { JobOrderMethod, JobOrderStatus } from '@gh/builder-shared-data';
import {
  CELL_TEXT_CENTER_CLASS,
  conditionalCellRenderer,
  createDefaultGridModel,
  dictionaryValueFormatter,
  rendererIconByName,
} from '@gh/common-internal-shared';
import {
  DateTimeTypes,
  EnumDictionary,
  getRouteInitData,
  IS_REGION_NZ,
  NumberTypes,
} from '@gh/common-shared';
import { bySelectionModel, SelectionModel } from '@gh/core-data';
import { TranslationService } from '@gh/core-mls';
import { STYLES_FLEX_COLUMN_LAYOUT } from '@gh/core-ui';
import {
  buildDateTimeColumn,
  buildNumberColumn,
  GridDefinition,
  GridDefinitionFactory,
} from '@gh/core-ui-grid';

import { isEmpty, negate } from 'lodash';
import { BehaviorSubject } from 'rxjs';

import {
  getJobOrderListItemId,
  isJobOrderProduced,
  JobOrderListItem,
} from '../../../resources/job-order-list.model';
import { ID } from '@gh/common-shared-data';

@Component({
  standalone: false,
  selector: 'gh-job-order-details-body',
  templateUrl: 'job-order-details-body.component.html',
  styles: [STYLES_FLEX_COLUMN_LAYOUT],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JobOrderDetailsBodyComponent implements OnInit, OnChanges {
  @Input() orders: JobOrderListItem[];
  @Input() selection: SelectionModel;
  @Input() has6ADocument: SelectionModel;
  @Input() printRecordFormCriteria: boolean;

  @Output() preview = new EventEmitter<void>();
  @Output() selectionChange = new EventEmitter<SelectionModel>();
  @Output() has6ADocumentChange = new EventEmitter<SelectionModel>();

  isNzRegion = IS_REGION_NZ;

  gridDefinition: GridDefinition;
  total: number;
  orderStatusDict: EnumDictionary<JobOrderStatus>;
  checkList: any[] = [];
  checkListHas6A: any[] = [];

  private _editable$$ = new BehaviorSubject(true);

  @Input()
  set editable(editable: boolean) {
    this._editable$$.next(editable);
  }

  get editable(): boolean {
    return this._editable$$.value;
  }

  constructor(private route: ActivatedRoute,
              private translations: TranslationService,
              private gridDefinitionFactory: GridDefinitionFactory) {
  }

  ngOnInit(): void {
    const { orderStatusDict } = getRouteInitData(this.route);
    this.orderStatusDict = orderStatusDict;
    this.gridDefinition = this.buildGridDefinition();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // if selection has been changed we have to recalculate total
    if (changes['selection']) {
      if (!changes['selection'].currentValue?.idList?.length) {
        this.checkList = [];
      } else {
        this.checkList = changes['selection'].currentValue?.idList;
      }
      this.recalculateTotal();
    }
    if (changes['has6ADocument']) {
      if (!changes['has6ADocument'].currentValue?.idList?.length) {
        this.checkListHas6A = [];
      } else {
        this.checkListHas6A = changes['has6ADocument'].currentValue?.idList;
      }
      this.recalculateTotal();
    }
  }
  onCheckChange(id: ID): void {
    const targetItem = this.orders.find((item) => item.jobOrderId === id);
    if (!targetItem || !targetItem.isCanBeSelected) {
      return;
    }
    if (this.checkList.includes(id)) {
      this.checkList.splice(this.checkList.indexOf(id), 1);
      this.checkList = [...this.checkList];
      this.selectionChange.emit({ isExcludeMode: false, idList: this.checkList });
      return;
    }
    this.checkList = [...this.checkList, id];
    this.selectionChange.emit({ isExcludeMode: false, idList: this.checkList });
  }
  onPreview(): void {
    this.preview.emit();
  }

  onSelectionChange(selection: SelectionModel): void {
    this.selectionChange.emit(selection);
  }

  isOrderEnabled(order: JobOrderListItem): boolean {
    return order.isCanBeSelected;
  }

  is6ADisabled(order: JobOrderListItem): boolean {
    return !order.isCanBeSelected || !order.is6A;
  }

  on6aDocumentSelectionChange(id: ID): void {
    const targetItem = this.orders.find((item) => item.jobOrderId === id);
    if (!targetItem || !targetItem.isCanBeSelected || !targetItem.is6A) {
      return;
    }
    if (this.checkListHas6A.includes(id)) {
      this.checkListHas6A.splice(this.checkListHas6A.indexOf(id), 1);
      this.checkListHas6A = [...this.checkListHas6A];
      this.has6ADocumentChange.emit({ isExcludeMode: false, idList: this.checkListHas6A });
      return;
    }
    this.checkListHas6A = [...this.checkListHas6A, id];
    this.has6ADocumentChange.emit({ isExcludeMode: false, idList: this.checkListHas6A });
  }

  private buildGridDefinition(): GridDefinition {
    const { jobOrderGridLayout, sendOrderByDict } = getRouteInitData(this.route);
    const model = createDefaultGridModel({
      getId: getJobOrderListItemId,
    });
    const sort = (a, b) => {
      if (a < b) {
        return -1;
      }
      if (a > b) {
        return 1;
      }
      return 0;
    };
    return this.gridDefinitionFactory.buildLayoutBasedWithModel({
      model,
      layout: jobOrderGridLayout,
      columns: {
        orderNumber: {
          cellClass:  CELL_TEXT_CENTER_CLASS,
          comparator: sort,
        },
        startDate: {
          ...buildDateTimeColumn(DateTimeTypes.ShortDate),
          comparator: sort,
          valueGetter: ({ data }: { data: JobOrderListItem }) => data.deliveryDate,
        },
        contractReceived: {
          ...buildDateTimeColumn(DateTimeTypes.ShortDate),
          comparator: sort,
          valueGetter: ({ data }: { data: JobOrderListItem }) => data.contractEnd,
        },
        costCenterName: {
          comparator: sort,
        },
        costCenterCode: {
          comparator: sort,
        },
        amount: {
          ...buildNumberColumn(NumberTypes.SignedPrice),
          comparator: sort,
        },
        attachments: {
          cellRenderer: conditionalCellRenderer(negate(isEmpty), rendererIconByName('attach-file')),
          comparator: sort,
        },
        supplierName: {
          comparator: sort,
        },
        sendByStatus: {
          minWidth: 95,
          comparator: sort,
          valueGetter: ({ data }: { data: JobOrderListItem }) =>
            isJobOrderProduced(data)
              ? ({
                [JobOrderMethod.Email]: this.translations.get('lbl_emailed'),
                [JobOrderMethod.Printer]: this.translations.get('lbl_printed'),
              }[data.producedMethod!] ?? '')
              : sendOrderByDict.get(data.method) ?? '',
          cellClassRules: {
            'active-status': ({ data, value }: { data: JobOrderListItem; value?: string }) =>
              isJobOrderProduced(data) && !!String(value ?? '').trim(),
            'inactive-status': ({ data, value }: { data: JobOrderListItem; value?: string }) =>
              !isJobOrderProduced(data) && !!String(value ?? '').trim(),
          },
        },
        producedDate: {
          ...buildDateTimeColumn(DateTimeTypes.ShortDate),
          comparator: sort,
        },
        status: {
          minWidth: 95,
          comparator: sort,
          valueFormatter: dictionaryValueFormatter(this.orderStatusDict),
          cellClassRules: {
            'processing-status': ({ data }: { data: JobOrderListItem }) => data.status === JobOrderStatus.Pending,
            'active-status': ({ data }: { data: JobOrderListItem }) => data.status === JobOrderStatus.Accepted,
            'deleted-status': ({ data }: { data: JobOrderListItem }) => data.status === JobOrderStatus.Rejected,
          },
        },
      },
    });
  }

  private recalculateTotal(): void {
    this.total = this.orders
      .filter(bySelectionModel(this.selection, getJobOrderListItemId))
      .reduce((sum, order) => sum + order.amount, 0);
  }
}
